import { PIN_HOVER_CSS } from '../../../utils/pinHoverCss.js';

// Shared Leaflet CSS styles for both tracking and weather maps

export const getLeafletStyles = (isDark, styleId) => `
  .leaflet-popup-content-wrapper {
    background-color: ${isDark ? '#1d1d1d' : '#ffffff'};
    color: ${isDark ? '#f0f5f2' : '#1f2937'};
    --harvest-not-producing-text: ${isDark ? '#9CA3AF' : '#6B7280'};
    border-radius: 8px;
    border: 1px solid ${isDark ? '#333' : '#e5e7eb'};
  }
  .leaflet-popup-tip {
    background-color: ${isDark ? '#1d1d1d' : '#ffffff'};
    border: 1px solid ${isDark ? '#333' : '#e5e7eb'};
  }
  .leaflet-popup-close-button {
    color: ${isDark ? '#888' : '#6b7280'} !important;
  }
  .leaflet-popup-close-button:hover {
    color: var(--bloom-green) !important;
  }
  .leaflet-container {
    background-color: ${isDark ? '#1d1d1d' : '#f3f4f6'};
  }
  .leaflet-control-attribution {
    background-color: ${isDark ? 'rgba(29, 29, 29, 0.8)' : 'rgba(255, 255, 255, 0.8)'} !important;
    color: ${isDark ? '#666' : '#6b7280'} !important;
    font-size: 10px !important;
  }
  .leaflet-control-attribution a {
    color: var(--bloom-green) !important;
  }
  .leaflet-control-zoom a {
    background-color: ${isDark ? '#282a29' : '#ffffff'} !important;
    color: ${isDark ? '#f0f5f2' : '#1f2937'} !important;
    border-color: ${isDark ? '#333' : '#d1d5db'} !important;
  }
  .leaflet-control-zoom a:hover {
    background-color: var(--bloom-green) !important;
    color: ${isDark ? '#1d1d1d' : '#ffffff'} !important;
  }
  .ship-marker {
    background: transparent !important;
    border: none !important;
  }
  .port-label {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    font-size: 10px;
    font-weight: 600;
    color: ${isDark ? '#d1d5db' : '#4b5563'};
  }
  .port-label::before {
    display: none;
  }
  .port-tooltip {
    font-size: 11px;
    font-weight: 600;
  }
  .weather-map-container .leaflet-interactive {
    cursor: pointer !important;
    -webkit-tap-highlight-color: rgba(21, 203, 94, 0.3) !important;
    touch-action: manipulation !important;
  }
  .weather-map-container .leaflet-tile-pane {
    pointer-events: none !important;
  }
  .leaflet-pin-icon {
    background: transparent !important;
    border: none !important;
  }
  /* Leaflet makes interactive markers focusable, so clicking a pin left a
     browser focus square around its box. Dropped for pointer focus only —
     keyboard focus keeps its ring, as on .hazard-icon below. */
  .leaflet-pin-icon:focus:not(:focus-visible) {
    outline: none;
  }
  ${PIN_HOVER_CSS}
  .hazard-icon svg {
    display: block;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35));
    opacity: 0.8; /* let the precipitation layer show through */
    transition: opacity 0.15s;
  }
  .hazard-icon:focus:not(:focus-visible) {
    outline: none; /* no focus square after a mouse click; keyboard focus keeps its ring */
  }
  .hazard-icon:hover svg {
    opacity: 1;
  }
  .leaflet-pin-icon--muted {
    opacity: 0.9;
  }
  .storm-icon {
    background: transparent !important;
    border: none !important;
  }
  .storm-icon svg {
    display: block;
    filter: drop-shadow(0 1px 1.5px rgba(15, 23, 42, 0.35));
  }
  /* The glyph is scaled by a centred inner wrapper (24..48 px equivalent); Leaflet's outer transform is untouched. */
  .storm-icon__inner {
    transform-origin: 50% 50%;
    will-change: transform;
  }
  .storm-icon--glide {
    transition: transform var(--storm-glide, 1500ms) linear;
  }
  @media (prefers-reduced-motion: reduce) {
    .storm-icon--glide,
    .storm-icon__inner {
      transition: none !important;
    }
  }
  /* Hazard panes never take pointer events; the track, wind zones and chips carry no blur or shadow. */
  .leaflet-hazard-fill-pane,
  .leaflet-hazard-line-pane,
  .leaflet-hazard-label-pane,
  .leaflet-hazard-fill-pane *,
  .leaflet-hazard-line-pane *,
  .leaflet-hazard-label-pane * {
    pointer-events: none !important;
  }
  .hazard-fade {
    transition: stroke-opacity 150ms ease-out, fill-opacity 150ms ease-out;
  }
  @media (prefers-reduced-motion: reduce) {
    .hazard-fade { transition: none; }
  }
  .hazard-pin-icon,
  .hazard-chip-icon {
    background: transparent !important;
    border: none !important;
  }
  .hazard-disc {
    position: absolute;
    left: 0;
    top: 0;
    box-sizing: border-box;
    width: 20.5px;
    height: 20.5px;
    margin: -10.25px 0 0 -10.25px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2.5px solid #ffffff;
    border-radius: 50%;
    color: #ffffff;
    font: 700 9px/1 Arial, Helvetica, sans-serif;
    letter-spacing: 0;
  }
  .hazard-disc--latest {
    width: 24.5px;
    height: 24.5px;
    margin: -12.25px 0 0 -12.25px;
  }
  .hazard-glyph {
    position: absolute;
    left: 0;
    top: 0;
    display: block;
    margin: -7px 0 0 -7px;
  }
  .hazard-glyph svg {
    display: block;
  }
  .hazard-vertex-label {
    position: absolute;
    left: 0;
    top: 0;
    box-sizing: border-box;
    white-space: nowrap;
    background: ${isDark ? 'rgba(20, 35, 47, 0.96)' : 'rgba(255, 255, 255, 0.96)'};
    color: ${isDark ? '#f8fafc' : '#172b36'};
    border: 1px solid ${isDark ? '#547083' : '#cbd5e1'};
    box-shadow: none;
    font: 700 12px/1.1 Arial, Helvetica, sans-serif;
    padding: 3px 6px;
    border-radius: 4px;
  }
  .hazard-vertex-label--active {
    border-color: #16b84e;
  }
  /* Pin hover.
   *
   * Done in CSS rather than by swapping the icon for a larger one, which is
   * how the sidebar's hover-to-enlarge works. A swapped divIcon replaces the
   * element outright, so there is nothing for a transition to animate between
   * and the pin jumps; transforming the element that is already there lets the
   * browser tween it, and costs no React render or icon-cache entry per pin.
   *
   * The origin is the tip, not the centre: a pin marks the point it stands on,
   * so scaling about the middle would slide that point as it grows. */
  /* THE SCALE GOES ON THE INNER <svg>, NOT THE MARKER.
   *
   * Leaflet positions every marker with an inline transform: translate3d(...)
   * on .leaflet-marker-icon, which is the same element our class sits on.
   * An inline style outranks a stylesheet rule, so a transform here is
   * dropped on the floor — and silently: the pin simply never grows. The glow
   * below was unaffected because filter is not transform, which is exactly
   * why one worked and the other did not. The SVG child is ours alone, so
   * scaling it is safe and immune to Leaflet rewriting the position. */
  .leaflet-pin-icon svg {
    transform-origin: 50% 100%;
    transition:
      transform 140ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 140ms cubic-bezier(0.22, 1, 0.36, 1);
    will-change: transform;
  }
  .leaflet-pin-icon--hover {
    z-index: 1000 !important;
  }
  .leaflet-pin-icon--hover svg {
    transform: scale(1.1);
  }
  /* The glow.
   *
   * drop-shadow, not box-shadow or a ::before circle: it is computed from the
   * element's alpha, so it hugs the teardrop instead of boxing it or sitting
   * behind it as a disc the pin's point sticks out of. Two stacked shadows —
   * a tight one for definition and a wide soft one for the halo.
   *
   * The colour rides in --pin-glow, set per pin from its own status colour, so
   * an ending-soon pin glows amber rather than every pin glowing green. The
   * fallback only applies if a pin is ever built without it. */
  .leaflet-pin-icon--hover svg {
    filter:
      drop-shadow(0 0 3px var(--pin-glow, #16A34A))
      drop-shadow(0 0 9px var(--pin-glow, #16A34A));
  }
  /* Clusters carry a resting drop shadow that single pins do not. It lives
     here rather than inline on the SVG because an inline filter outranks a
     stylesheet rule — inline, the hover glow below could never apply. Hover
     re-states it so the pin keeps its lift while gaining the halo. */
  .leaflet-pin-icon--cluster svg {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.28));
  }
  .leaflet-pin-icon--cluster.leaflet-pin-icon--hover svg {
    filter:
      drop-shadow(0 2px 4px rgba(0, 0, 0, 0.28))
      drop-shadow(0 0 3px var(--pin-glow, #16A34A))
      drop-shadow(0 0 10px var(--pin-glow, #16A34A));
  }

  /* Vessels, planes, trucks and hub stacks. The same treatment as the growing
     pins above — identical scale, duration and curve, and now the same
     two-layer glow, keyed to each marker's own --pin-glow so a delayed vessel
     halos red where an on-time one halos green. Hovering anything on the map
     should feel like hovering anything else on it; the earlier decision to
     give these scale only was reversed in review.
     Safe to transform the container: the heading rotation is an SVG attribute
     on an inner <g>, not a CSS transform here, so the two cannot overwrite
     each other. */
  /* Vessels and hub boxes are built with interactive: false, because their
     clicks are resolved at map level by ShipClickResolver rather than per
     marker. Leaflet only grants pointer events to markers carrying
     .leaflet-interactive, so those two never received any — which is why
     they alone had no hover while planes and trucks did.
     Granting it here is additive and does not make them Leaflet targets: with
     no .leaflet-interactive class Leaflet still lets the event bubble to the
     map, so the click resolver and map dragging keep working. */
  .ship-marker {
    pointer-events: auto;
  }
  .ship-marker svg,
  .mode-marker svg {
    transform-origin: 50% 50%;
    transition:
      transform 140ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 140ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  /* .ship-marker--hover is set by ShipMarker from the resolver's hover state:
     sea markers are pointer-events:none, so :hover alone never fires on them. */
  /* Deliberately NOT .mode-marker:hover. Planes and trucks are raised by the
     mode layer's own zIndexOffset (60000 at rest, 150000 hovered/selected),
     which Leaflet writes inline — and an inline z-index loses to this
     !important rule. Applying it to them therefore DROPPED the hovered marker
     from ~60000 to 1000, behind every other marker; whatever then covered the
     cursor ended the :hover, which restored the z-index, which re-entered the
     :hover — a strobe for as long as the cursor sat on a plane. Ships live in
     a 0-2000 band where 1000 is a genuine lift, so they keep it. */
  .ship-marker:hover,
  .ship-marker--hover {
    z-index: 1000 !important;
  }
  .ship-marker:hover svg,
  .ship-marker--hover svg,
  .mode-marker:hover svg {
    transform: scale(1.1);
    filter:
      drop-shadow(0 0 3px var(--pin-glow, #16A34A))
      drop-shadow(0 0 9px var(--pin-glow, #16A34A));
  }

  @media (prefers-reduced-motion: reduce) {
    .leaflet-pin-icon svg,
    .ship-marker svg,
    .mode-marker svg {
      transition: none;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .leaflet-pin-icon {
      transition: none;
    }
  }
  .weather-map-container .leaflet-top,
  .weather-map-container .leaflet-bottom {
    z-index: 1000 !important;
  }

  .produce-map-controls .leaflet-control-zoom {
    display: flex !important;
    flex-direction: column;
    gap: 2px;
    border: none !important;
    box-shadow: none !important;
    margin: 12px 0 0 12px !important;
  }
  .produce-map-controls .leaflet-control-zoom a {
    box-sizing: border-box !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    width: 28px !important;
    height: 28px !important;
    line-height: 1 !important;
    border: 1px solid ${isDark ? '#30363d' : '#E2E6E2'} !important;
    border-radius: 6px !important;
    background-color: ${isDark ? '#282a29' : '#ffffff'} !important;
    color: ${isDark ? '#f0f5f2' : '#5D625F'} !important;
    font-size: 16px !important;
    font-weight: 400 !important;
    box-shadow: none !important;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  }
  .produce-map-controls .leaflet-control-zoom a:hover {
    background-color: ${isDark ? '#333534' : '#F0F4F1'} !important;
    color: ${isDark ? '#ffffff' : '#282A29'} !important;
  }
  .produce-map-controls .leaflet-control-zoom a.leaflet-disabled {
    background-color: ${isDark ? '#282a29' : '#ffffff'} !important;
    border-color: ${isDark ? '#30363d' : '#E2E6E2'} !important;
    color: ${isDark ? '#5d625f' : '#C3C4C3'} !important;
  }
`;

export const injectLeafletStyles = (isDark, styleId) => {
  let styleEl = document.getElementById(styleId);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = getLeafletStyles(isDark, styleId);
  return () => { styleEl.remove(); };
};
